"use client";

/**
 * InstallCheckin — the return-visit half of the install-success loop.
 *
 * The receipt asks "did it work?" seconds after download, but most users
 * haven't been to their car yet — which is why install confirmations sat at
 * ~zero. This component records a pending install at download time
 * (recordPendingInstall, called from the post-download receipt) and asks on a
 * LATER visit, when the answer actually exists: any sounds-page visit between
 * 2 hours and 14 days after a download, one quiet banner, one question.
 *
 * Outcomes feed the same install_self_reported_complete funnel event as the
 * other surfaces, tagged source: 'return_visit'. "Had trouble" routes to the
 * troubleshooting guide. Answers (and dismissals) are remembered per sound in
 * localStorage — nobody gets asked twice.
 *
 * Install copy is sacred-path territory (PRODUCT.md #5): no jokes here.
 */

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { Check, X } from "lucide-react";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import { InstallSuccessNewsletter } from "@/components/ui/install-success-newsletter";
import { SUPPORT_EMAIL, supportMailto } from "@/lib/contact";
import { trackInstallSelfReportedComplete } from "@/lib/tracking";
import { MOTION_DURATION, MOTION_EASE } from "@/lib/motion-theme";
import { readStorage, removeStorage, storageKeys, writeStorage } from "@/lib/safe-storage";

const PENDING_PREFIX = "tls.pendingInstall.";
const ANSWERED_PREFIX = "tls.installReport.";

/** Ask no earlier than 2h after download (they need to reach the car)... */
const MIN_AGE_MS = 2 * 60 * 60 * 1000;
/** ...and no later than 14 days (stale asks are noise). */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

interface PendingInstall {
  soundId: string;
  title: string;
  t: number;
}

/** Called at download time (post-download receipt) to arm the check-in. */
export function recordPendingInstall(soundId: string, title: string): void {
  if (readStorage(ANSWERED_PREFIX + soundId)) return;
  // Storage unavailable (private mode, WebView with DOM storage off) — the
  // write no-ops and the loop just doesn't arm.
  writeStorage(
    PENDING_PREFIX + soundId,
    JSON.stringify({ title, t: Date.now() })
  );
}

/** Mark a sound's install question as answered so no surface re-asks. */
export function markInstallAnswered(soundId: string, outcome: string): void {
  writeStorage(ANSWERED_PREFIX + soundId, outcome);
  removeStorage(PENDING_PREFIX + soundId);
}

export function hasAnsweredInstall(soundId: string): boolean {
  return readStorage(ANSWERED_PREFIX + soundId) !== null;
}

function findDuePendingInstall(now: number): PendingInstall | null {
  let best: PendingInstall | null = null;
  for (const key of storageKeys()) {
    if (!key.startsWith(PENDING_PREFIX)) continue;
    const soundId = key.slice(PENDING_PREFIX.length);
    let parsed: { title?: unknown; t?: unknown };
    try {
      parsed = JSON.parse(readStorage(key) ?? "");
    } catch {
      removeStorage(key);
      continue;
    }
    if (typeof parsed?.t !== "number" || typeof parsed?.title !== "string") {
      removeStorage(key);
      continue;
    }
    const age = now - parsed.t;
    if (age > MAX_AGE_MS) {
      removeStorage(key);
      continue;
    }
    if (age < MIN_AGE_MS) continue;
    if (!best || parsed.t > best.t) {
      best = { soundId, title: parsed.title, t: parsed.t };
    }
  }
  return best;
}

/**
 * §01.9: dates are absolute ("24 Jul 2026"), never relative — "yesterday"
 * ages badly in a strip that can sit unanswered for up to 14 days.
 */
function absoluteDay(t: number): string {
  return new Date(t).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function InstallCheckin({ className }: { className?: string }) {
  const [pending, setPending] = useState<(PendingInstall & { dayLabel: string }) | null>(null);
  const [answered, setAnswered] = useState<"success" | "failed" | null>(null);
  const prefersReducedMotion = useReducedMotion();

  // Client-only scan after mount — localStorage isn't available during SSR,
  // and rendering nothing on the server keeps hydration clean. The relative
  // day label is computed here too so render stays pure (no Date.now()).
  useEffect(() => {
    const now = Date.now();
    const found = findDuePendingInstall(now);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration-safe reveal: SSR + first client render agree on null, then the banner appears
    setPending(found ? { ...found, dayLabel: absoluteDay(found.t) } : null);
  }, []);

  const answer = useCallback(
    (outcome: "success" | "failed") => {
      if (!pending) return;
      markInstallAnswered(pending.soundId, outcome);
      trackInstallSelfReportedComplete({
        outcome,
        sound_id: pending.soundId,
        source: "return_visit",
        came_from_download: true,
      });
      setAnswered(outcome);
    },
    [pending]
  );

  const dismiss = useCallback(() => {
    if (!pending) return;
    markInstallAnswered(pending.soundId, "dismissed");
    setPending(null);
  }, [pending]);

  if (!pending) return null;

  return (
    <div
      role="status"
      className={className}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 10,
        // De-carded: a status strip is content. It reads as one line of the
        // page rather than a tile parked on it.
        padding: "12px 0",
        borderTop: "1px solid var(--tls-border-subtle)",
        borderBottom: "1px solid var(--tls-border-subtle)",
        fontSize: 13.5,
        color: "var(--tls-fg-muted)",
      }}
    >
      <LazyMotion features={domAnimation}>
        <AnimatePresence initial={false} mode="wait">
          <m.div
            key={answered ?? "prompt"}
            initial={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, transform: "translateY(4px)" }
            }
            animate={{
              opacity: 1,
              ...(prefersReducedMotion ? {} : { transform: "translateY(0px)" }),
            }}
            exit={{
              opacity: 0,
              ...(prefersReducedMotion ? {} : { transform: "translateY(-4px)" }),
            }}
            transition={{ duration: MOTION_DURATION.fast, ease: MOTION_EASE.emphasis }}
            style={{
              display: "flex",
              flex: "1 1 100%",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
            }}
          >
            {answered === "success" ? (
              <div style={{ flex: "1 1 100%" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Check className="size-4" style={{ color: "var(--tls-brand-ink)" }} aria-hidden="true" />
                  Enjoy the new chime.
                </span>
                <InstallSuccessNewsletter surface="return_visit" className="mt-3" />
              </div>
            ) : answered === "failed" ? (
              <span>
                Sorry about that. The{" "}
                <Link
                  href="/blog/troubleshooting-tesla-custom-sounds"
                  style={{ color: "var(--tls-fg-strong)", textDecoration: "underline" }}
                >
                  7 most common fixes
                </Link>{" "}
                solve nearly every case. Still stuck? Email{" "}
                <a
                  href={supportMailto("Tesla lock sound install help")}
                  style={{ color: "var(--tls-fg-strong)", textDecoration: "underline" }}
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </span>
            ) : (
              <>
                <span style={{ flex: "1 1 auto", minWidth: 200 }}>
                  You grabbed <strong style={{ color: "var(--tls-fg-strong)" }}>{pending.title}</strong>{" "}
                  on {pending.dayLabel}: did the install work?
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--tls-fg-subtle)" }}>
                    Something off? Email{" "}
                    <a
                      href={supportMailto("Tesla lock sound install help")}
                      className="tlsr-focus underline underline-offset-2"
                      style={{ color: "var(--tls-fg-muted)" }}
                    >
                      {SUPPORT_EMAIL}
                    </a>
                    .
                  </span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => answer("success")}
                    className={checkinBtnHitArea}
                    style={checkinBtnStyle}
                  >
                    It worked
                  </button>
                  <button
                    type="button"
                    onClick={() => answer("failed")}
                    className={checkinBtnHitArea}
                    style={checkinBtnStyle}
                  >
                    Had trouble
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss install check-in"
                    // 28px painted; ::before pads the target to 44 on both axes.
                    className="tlsr-focus relative before:absolute before:-inset-2 before:content-['']"
                    style={{
                      display: "inline-flex",
                      padding: 6,
                      background: "none",
                      border: "none",
                      color: "var(--tls-fg-subtle)",
                      cursor: "pointer",
                    }}
                  >
                    <X className="size-4" aria-hidden="true" />
                  </button>
                </span>
              </>
            )}
          </m.div>
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
}

/**
 * §02.4: "Pill height 36 · hit area padded to 44 vertically" — the painted
 * box stays 36, the ::before pads it to 44 (the same pattern as
 * components/ui/button.tsx `sm`). 5px each side, not 4: the pseudo-element
 * is inset from the padding box, so the pill's 1px border is outside it.
 */
const checkinBtnHitArea =
  "tlsr-focus relative before:absolute before:inset-x-0 before:-inset-y-[5px] before:content-['']";

const checkinBtnStyle: React.CSSProperties = {
  minHeight: 36,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--tls-radius-pill)",
  border: "1px solid var(--tls-border-subtle)",
  background: "var(--tls-surface-elevated)",
  color: "var(--tls-fg-strong)",
  cursor: "pointer",
};
